/**
 * Fast Mark Page Component
 * Quick marking interface for teachers
 * Upload file, select learner/assignment/rubric, get instant AI feedback
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControl, InputLabel,
  Select, MenuItem, Autocomplete, TextField, Card, CardContent,
  Divider, LinearProgress, Chip, Tabs, Tab
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import { Learner, Assignment, Rubric, FastMarkResponse } from '../../types/fastMark';
import MoodleImportContent from './MoodleImportContent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`upload-tabpanel-${index}`}
      aria-labelledby={`upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const FastMarkPage: React.FC = () => {
  const navigate = useNavigate();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Loading states
  const [loadingData, setLoadingData] = useState(true);
  const [marking, setMarking] = useState(false);

  // Data lists
  const [learners, setLearners] = useState<Learner[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);

  // Form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLearner, setSelectedLearner] = useState<Learner | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [selectedRubric, setSelectedRubric] = useState<Rubric | null>(null);

  // Results state
  const [result, setResult] = useState<FastMarkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // File drag & drop
  const [isDragging, setIsDragging] = useState(false);

  // Load data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [learnersRes, assignmentsRes, rubricsRes] = await Promise.all([
          axios.get('/api/learners/'),
          axios.get('/api/assignments/'),
          axios.get('/api/rubrics/')
        ]);

        setLearners(learnersRes.data.results || []);
        setAssignments(assignmentsRes.data.results || []);
        setRubrics(rubricsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please refresh the page.');
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Auto-select rubric when assignment changes
  useEffect(() => {
    if (selectedAssignment?.rubric_id) {
      const rubric = rubrics.find(r => r.id === selectedAssignment.rubric_id);
      if (rubric) {
        setSelectedRubric(rubric);
      }
    }
  }, [selectedAssignment, rubrics]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    handleReset();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);

    // Validation
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    if (!selectedLearner) {
      setError('Please select a learner');
      return;
    }
    if (!selectedAssignment) {
      setError('Please select an assignment');
      return;
    }
    if (!selectedRubric) {
      setError('Please select a rubric');
      return;
    }

    setMarking(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('learner_id', selectedLearner.id.toString());
      formData.append('assignment_id', selectedAssignment.id.toString());
      formData.append('rubric_id', selectedRubric.id.toString());

      const response = await axios.post<FastMarkResponse>(
        '/api/fast-mark/initiate/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000, // 2 minutes
        }
      );

      setResult(response.data);
    } catch (err: any) {
      console.error('Error marking submission:', err);

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.code === 'ECONNABORTED') {
        setError('Request timed out. The file may be too large. Please try again or use a smaller file.');
      } else {
        setError('Failed to mark submission. Please try again.');
      }
    } finally {
      setMarking(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedLearner(null);
    setSelectedAssignment(null);
    setSelectedRubric(null);
    setResult(null);
    setError(null);
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Fast Mark</Typography>
      </Breadcrumbs>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Fast Mark</Typography>
          <Typography variant="body2" color="textSecondary">
            Upload and mark submissions with AI-powered instant feedback
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<GroupsIcon />}
          onClick={() => navigate('/fast-mark-pipeline')}
        >
          Batch pipeline
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="upload method tabs">
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label="Single Learner"
            id="upload-tab-0"
            aria-controls="upload-tabpanel-0"
          />
          <Tab
            icon={<GroupsIcon />}
            iconPosition="start"
            label="Bulk Import (Moodle)"
            id="upload-tab-1"
            aria-controls="upload-tabpanel-1"
          />
        </Tabs>
      </Paper>

      {/* Single File Upload Tab */}
      <TabPanel value={tabValue} index={0}>
        {result && result.success && (
          <Card sx={{ mb: 3, bgcolor: 'success.light', borderColor: 'success.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6" color="success.dark">Marking Complete</Typography>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">AI Score</Typography>
                  <Typography variant="h4" color="success.dark">{result.ai_score}</Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>Feedback</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {result.ai_feedback}
                    </Typography>
                  </Paper>
                </Box>

                <Box display="flex" gap={2}>
                  <Button variant="outlined" color="success" onClick={handleReset}>
                    Mark Another
                  </Button>
                  {result.submission_id && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => navigate(`/submissions/${result.submission_id}`)}
                    >
                      View Full Submission
                    </Button>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {result && !result.success && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Marking Failed</Typography>
            <Typography variant="body2">{result.error}</Typography>
            {result.submission_id && (
              <Typography variant="caption" display="block" mt={1}>
                Submission ID: {result.submission_id}
              </Typography>
            )}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* File Upload */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                1. Upload Submission File
              </Typography>
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.main' : 'grey.400',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: isDragging ? 'action.hover' : 'background.default',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.txt,.html,.htm"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    {selectedFile ? selectedFile.name : 'Drag & drop a file here, or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Supported formats: PDF, DOC, DOCX, TXT, HTML
                  </Typography>
                  {selectedFile && (
                    <Chip
                      label={`${(selectedFile.size / 1024).toFixed(2)} KB`}
                      size="small"
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  )}
                </label>
              </Box>
            </Box>

            <Divider />

            {/* Learner Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                2. Select Learner
              </Typography>
              <Autocomplete
                options={learners}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.student_id})`}
                value={selectedLearner}
                onChange={(_, newValue) => setSelectedLearner(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Learner"
                    placeholder="Search by name or student ID"
                  />
                )}
                disabled={marking}
              />
            </Box>

            {/* Assignment Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                3. Select Assignment
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Assignment</InputLabel>
                <Select
                  value={selectedAssignment?.id || ''}
                  onChange={(e) => {
                    const assignment = assignments.find(a => a.id === Number(e.target.value));
                    setSelectedAssignment(assignment || null);
                  }}
                  label="Assignment"
                  disabled={marking}
                >
                  {assignments.map((assignment) => (
                    <MenuItem key={assignment.id} value={assignment.id}>
                      {assignment.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedAssignment && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedAssignment.description} • Due: {new Date(selectedAssignment.due_date).toLocaleDateString()} • Max Points: {selectedAssignment.max_points}
                </Typography>
              )}
            </Box>

            {/* Rubric Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                4. Select Rubric
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Rubric</InputLabel>
                <Select
                  value={selectedRubric?.id || ''}
                  onChange={(e) => {
                    const rubric = rubrics.find(r => r.id === Number(e.target.value));
                    setSelectedRubric(rubric || null);
                  }}
                  label="Rubric"
                  disabled={marking}
                >
                  {rubrics.map((rubric) => (
                    <MenuItem key={rubric.id} value={rubric.id}>
                      {rubric.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedRubric && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedRubric.description}
                </Typography>
              )}
            </Box>

            {marking && (
              <Box>
                <LinearProgress sx={{ mb: 1 }} />
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  Marking in progress... This may take a minute or two.
                </Typography>
              </Box>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={marking}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={marking || !selectedFile || !selectedLearner || !selectedAssignment || !selectedRubric}
                startIcon={marking ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {marking ? 'Marking...' : 'Mark Submission'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </TabPanel>

      {/* Bulk Import Tab */}
      <TabPanel value={tabValue} index={1}>
        <MoodleImportContent />
      </TabPanel>
    </Box>
  );
};

export default FastMarkPage;
